import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CheckCircle, Eye, EyeOff } from 'lucide-react';
import api from '../../lib/api';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-bold text-emerald-700 mb-8" style={{ fontFamily: 'Outfit' }}>Jjuma Pay</h1>

        {done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={28} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Password reset!</h2>
            <p className="text-slate-500 text-sm mb-6">Your password has been reset successfully.</p>
            <Link to="/login" data-testid="go-to-login" className="text-emerald-700 font-medium hover:underline">Sign in now</Link>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Reset your password</h2>
            <p className="text-slate-500 text-sm mb-6">Enter your new password below</p>

            {!token && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">Invalid reset link. Please request a new one.</div>}
            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-error">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-slate-700">New Password</Label>
                <div className="relative mt-1.5">
                  <Input type={showPw ? 'text' : 'password'} data-testid="reset-password-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" required />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-slate-700">Confirm Password</Label>
                <Input type="password" data-testid="reset-confirm-input" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm password" className="mt-1.5" required />
              </div>
              <Button type="submit" data-testid="reset-submit-btn" disabled={loading || !token} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
